import { Box, Group, useMantineTheme } from '@mantine/core';
import AutoScroll from 'embla-carousel-auto-scroll';
import useEmblaCarousel from 'embla-carousel-react';

// Company data for the carousel
const COMPANY_DATA = [
	{ name: '6sense', logo: '/images/brands/6sense.svg' },
	{ name: 'AFG', logo: '/images/brands/afg.svg' },
	{ name: 'Alegeus', logo: '/images/brands/alegeus.svg' },
	{ name: 'Cargurus', logo: '/images/brands/cargurus.svg' },
	{ name: 'CFL', logo: '/images/brands/cfl.svg' },
	{ name: 'Chipotle', logo: '/images/brands/chipotle.svg' },
	{ name: 'Clay', logo: '/images/brands/clay.svg' },
	{ name: 'Clover', logo: '/images/brands/clover.svg' },
	{ name: 'DailyPay', logo: '/images/brands/dailypay.svg' },
	{ name: 'Deezer', logo: '/images/brands/deezer.svg' },
	{ name: 'Dialpad', logo: '/images/brands/dialpad.svg' },
	{ name: 'Hippo', logo: '/images/brands/hippo.svg' },
	{ name: 'IQVIA', logo: '/images/brands/iqvia.svg' },
	{ name: 'Kaufland', logo: '/images/brands/kaufland.svg' },
	{ name: 'LegalShield', logo: '/images/brands/legalshield.svg' },
	{ name: 'Lichtblick', logo: '/images/brands/lichtblick.svg' },
	{ name: 'Loop', logo: '/images/brands/loop.svg' },
	{ name: 'Mejuri', logo: '/images/brands/mejuri.svg' },
	{ name: 'MoonPay', logo: '/images/brands/moonpay.svg' },
	{ name: 'Oversight', logo: '/images/brands/oversight.svg' },
	{ name: 'Pendo', logo: '/images/brands/pendo.svg' },
	{ name: 'Remote', logo: '/images/brands/remote.svg' },
	{ name: 'Remitly', logo: '/images/brands/remitly.svg' },
	{ name: 'Ruggable', logo: '/images/brands/ruggable.svg' },
	{ name: 'Super', logo: '/images/brands/super.svg' },
	{ name: 'TogetherAI', logo: '/images/brands/togetherai.svg' },
	{ name: 'Upsell', logo: '/images/brands/upsell.svg' },
	{ name: 'Univision', logo: '/images/brands/univision.svg' },
	{ name: 'Vanta', logo: '/images/brands/vanta.svg' },
	{ name: 'Wave', logo: '/images/brands/wave.svg' },
];

function rgbToRgbaString(rgbString: string, alpha: number): string {
	// Regex to match 'rgb(r, g, b)' or 'rgb(r g b)'
	const match = rgbString.match(/(\d{1,3}),? ?(\d{1,3}),? ?(\d{1,3})/);

	if (!match) {
		throw new Error('Invalid RGB string format.');
	}

	// Extract the r, g, and b values using array destructuring
	const [, r, g, b] = match;

	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function CompanyNamesCarousel({
	backgroundColor,
	withG2,
}: {
	backgroundColor?: string;
	withG2?: boolean;
}) {
	const theme = useMantineTheme();

	const [emblaRef] = useEmblaCarousel(
		{
			align: 'start',
			skipSnaps: true,
			loop: true,
		},
		[AutoScroll({ playOnInit: true, speed: 1 })]
	);

	const emblaStyles = {
		margin: 'auto',
		position: 'relative',
		width: withG2 ? 'calc(100% - 117px)' : '100%',
	} as React.CSSProperties;

	const viewportStyles = {
		overflow: 'hidden',
	} as React.CSSProperties;

	const containerStyles = {
		display: 'flex',
		touchAction: 'pan-y pinch-zoom',
		marginLeft: 'calc(1rem * -1)',
	} as React.CSSProperties;

	const slideStyles = {
		flex: '0 0 auto',
		minWidth: 0,
		paddingLeft: '2rem',
	} as React.CSSProperties;

	const background =
		backgroundColor || theme.other.getColor('surface/secondary/default');

	const blurOverlayStyles = {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		pointerEvents: 'none',
		background: `linear-gradient(90deg, ${rgbToRgbaString(background, 1)} 0%, ${rgbToRgbaString(background, 0.1)} 10%, ${rgbToRgbaString(background, 0.1)} 90%, ${rgbToRgbaString(background, 1)} 100%)`,
		zIndex: 1,
		borderRadius: '4px',
	} as React.CSSProperties;

	return (
		<Group spacing="md" align="center" noWrap>
			{/* Fixed G2 logo with stars */}
			{withG2 && (
				<Group spacing="xs" align="center" style={{ flexShrink: 0 }}>
					<img
						src="/images/brands/g2.svg"
						alt="G2 logo"
						style={{ height: '20px', width: '20px' }}
					/>
					<img
						src="/images/brands/g2_stars.svg"
						alt="G2 rating stars"
						style={{ height: '16px', width: '97px' }}
					/>
				</Group>
			)}

			{/* Scrollable company carousel */}
			<Box style={emblaStyles}>
				<Box style={viewportStyles} ref={emblaRef}>
					<Box style={containerStyles}>
						{[...COMPANY_DATA].map((company) => (
							<img
								key={company.name}
								src={company.logo}
								alt={`${company.name} logo`}
								style={slideStyles}
							/>
						))}
					</Box>
				</Box>
				<Box style={blurOverlayStyles} />
			</Box>
		</Group>
	);
}
